/**
 * Are this listing's fitment facts complete, and do they agree with each other?
 * (P25009-398, BR-6 / AC-5)
 *
 * Two different questions, deliberately kept apart:
 *
 *   missingFitmentFacts       what is absent. The listing still sells, it is
 *                             just harder to find — a rim with no bolt pattern
 *                             cannot appear in any car search, only in a
 *                             dimension search. This drives the seller nudge.
 *
 *   contradictoryFitmentFacts what is present but cannot be true, like a tyre
 *                             marked 205/55R17 on a listing whose diameter says
 *                             18. This drives the import flag, because a
 *                             contradiction imported silently produces a
 *                             listing that shows up in the wrong searches and
 *                             quietly promises a fit it does not have.
 *
 * Absent is not the same as wrong, which is why they never share a list. Most
 * listings are missing something — hub bore is filled in on about 3% of rims —
 * and treating that as an error would bury the handful of rows that really are
 * broken.
 *
 * Written as CommonJS ON PURPOSE, like src/util/csvCore.js and
 * src/util/packageBundleCore.js: shared by the browser bundle (the seller
 * nudge) and the plain-Node API server (bulk upload and the supplier feed),
 * which is not transpiled and cannot import an ES module.
 */

const { boltCircleMm } = require('./boltCircleCore');

const clean = value => (typeof value === 'string' ? value.trim() : value == null ? '' : String(value));

const num = value => {
  const s = clean(value).replace(/_/g, '.').replace(/,/g, '.');
  if (!s) return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

const has = value => clean(value) !== '';

/* Plausible ranges for a road car. Wide on purpose: the job here is to catch a
 * column read into the wrong field or a decimal point lost at import, not to
 * second-guess an unusual but real fitment. */
const RANGES = {
  diameter: [10, 30], // inches
  tireWidth: [125, 405], // mm
  profile: [20, 95], // percent
  etFront: [-60, 90], // mm
  numberOfBolts: [3, 8],
  boltCircle: [95, 180], // mm
};

/* A bolt circle is read by boltCircleCore: `1397` is Console's own key for
 * 139.7, not a 1397 mm circle (P25009-488). */
const outOfRange = (key, value) => {
  const n = key === 'boltCircle' ? boltCircleMm(value) : num(value);
  if (n === null) return false; // absent is handled by missingFitmentFacts
  const [min, max] = RANGES[key];
  return n < min || n > max;
};

/** Which facts a listing of this kind needs to be found by a car search. */
const REQUIRED = {
  rim: [
    { key: 'numberOfBolts', label: 'antal bultar' },
    { key: 'boltCircle', label: 'bultcirkel' },
    { key: 'diameter', label: 'tumstorlek' },
    { key: 'etFront', label: 'ET' },
  ],
  tire: [
    { key: 'tireWidthFront', label: 'däckbredd' },
    { key: 'profileFront', label: 'profil' },
    { key: 'diameter', label: 'tumstorlek' },
  ],
};
// A complete wheel is a rim with tyres on it, so it needs both sets.
REQUIRED.wheel = [...REQUIRED.rim, ...REQUIRED.tire.filter(f => f.key !== 'diameter')];

/**
 * Facts this listing is missing, in plain Swedish.
 *
 * @param {Object} publicData
 * @returns {Array<string>} [] when nothing is missing, or the listing is a kind
 *   that carries no fitment (an accessory), in which case there is nothing to
 *   complete and nagging about it would be noise.
 */
const missingFitmentFacts = (publicData = {}) => {
  const required = REQUIRED[clean(publicData.adType)];
  if (!required) return [];
  return required.filter(f => !has(publicData[f.key])).map(f => f.label);
};

/**
 * Can this listing be found by a car search at all?
 *
 * Deliberately weaker than "nothing missing": a rim without ET still matches on
 * bolt pattern and diameter, and the buyer's search gives ET up before it gives
 * up the size. This is the line between "findable, could be better" and "only
 * findable by dimension".
 */
const isCarSearchable = (publicData = {}) => {
  const adType = clean(publicData.adType);
  if (adType === 'tire') {
    return has(publicData.tireWidthFront) && has(publicData.profileFront) && has(publicData.diameter);
  }
  if (adType === 'rim' || adType === 'wheel') {
    return has(publicData.numberOfBolts) && has(publicData.boltCircle) && has(publicData.diameter);
  }
  return true;
};

/**
 * Facts that are present but cannot all be true at once.
 *
 * @param {Object} publicData
 * @returns {Array<string>} human-readable problems, [] when consistent
 */
const contradictoryFitmentFacts = (publicData = {}) => {
  const problems = [];
  const adType = clean(publicData.adType);
  if (!REQUIRED[adType]) return problems;

  /* The tyre size carries its own diameter. When the listing also has a
   * diameter field and the two disagree, one of them is wrong and the listing
   * will show up in the wrong car searches whichever we believe. */
  const combination = Array.isArray(publicData.combination) ? publicData.combination : [];
  const sizeDiameters = combination
    .map(s => /R(\d{2})(?!\d)/i.exec(clean(s)))
    .filter(Boolean)
    .map(m => Number(m[1]));
  const diameter = num(publicData.diameter);
  if (diameter !== null && sizeDiameters.length && !sizeDiameters.includes(diameter)) {
    problems.push(
      `dimensionen (${combination.join(', ')}) stämmer inte med tumstorleken ${diameter}`
    );
  }

  // A bolt pattern is two numbers; half of one says nothing and matches nothing.
  const bolts = has(publicData.numberOfBolts);
  const circle = has(publicData.boltCircle);
  if (bolts !== circle) {
    problems.push(
      bolts ? 'bultcirkel saknas trots angivet antal bultar' : 'antal bultar saknas trots angiven bultcirkel'
    );
  }

  /* A rim bore narrower than a car hub is impossible to fit, and a value in the
   * wrong unit (inches instead of mm) lands here too. */
  const hub = num(publicData.hubHole);
  if (hub !== null && (hub < 40 || hub > 120)) {
    problems.push(`navhål ${hub} ligger utanför rimliga värden`);
  }

  Object.keys(RANGES).forEach(key => {
    const field = key === 'tireWidth' ? 'tireWidthFront' : key === 'profile' ? 'profileFront' : key;
    if (outOfRange(key, publicData[field])) {
      problems.push(`${field} ${clean(publicData[field])} ligger utanför rimliga värden`);
    }
  });

  return problems;
};

module.exports = {
  missingFitmentFacts,
  contradictoryFitmentFacts,
  isCarSearchable,
  REQUIRED,
  RANGES,
};
